import React from 'react';
import {
  Button, TextInput, EmptyState, Alert, Badge, Spinner, IconButton, useToast,
  useResponsive,
} from '@parsina/framework';
import { Pill, Trash2, AlertTriangle, Sparkles, X } from 'lucide-react';
import { C, CT } from '../../../constants/theme';
import { useTranslation } from '../../../i18n/I18nProvider.jsx';
import { bffQuery, bffMutation, errorCode } from '../../../lib/bffClient.js';
import {
  newRxItem, toMedicationInput, interactionIds, canSweep,
  severityKey, warningTypeKey, sortWarnings, allergenNames, conditionNames,
} from '../../../lib/prescription.js';
import {
  CHECK_DRUG_INTERACTION, CREATE_MEDICATION, SWEEP_DEBOUNCE_MS, SEVERITY_TINT,
  LIST_DRUGS, saveLabelKey,
} from '../../Prescription/Prescription.shared.js';
import DrugCombobox from '../../Prescription/DrugCombobox.jsx';
import MobileDrugSearch from '../../../components/mobile/MobileDrugSearch.jsx';
import MobileRxCard from '../../../components/mobile/MobileRxCard.jsx';
import { savedDosageText } from '../Visit.shared.js';

// §3.2.1 — دارو.
//
//   "Reuse Strategy: Inherit existing Doctor App Visit page medication data
//    model and UI component."
//
// Taken literally, and the reuse is real rather than a copy: `DrugCombobox`,
// `newRxItem`, `toMedicationInput`, `interactionIds`, `severityKey`,
// `sortWarnings` and `SEVERITY_TINT` are all imported from نسخه — the same
// modules that screen renders. §4's rule ("if the Doctor App uses a specific
// pattern for Adding a Medication, use that exact pattern") is satisfied by
// calling the same functions, not by writing a second implementation that looks
// like them.
//
// ── What this pane adds that نسخه does not have ────────────────────────────
// The APPOINTMENT. نسخه writes a prescription for a patient; this writes one
// for THIS VISIT, so every medication created here carries `appointmentId` and
// the list above it is the visit's own. That is the difference between "what
// this patient takes" and "what was prescribed today", and the visit screen
// needs both.
//
// ── The two rules نسخه exists to honour, inherited unchanged ───────────────
//  1. The interaction sweep NEVER blocks prescribing. It costs credits, it can
//     be exhausted, and it fails open — so a failed sweep is reported and the
//     ثبت button stays live.
//  2. The sweep is keyed on Drug.id SLUGS. A Persian display name reaches the
//     rule engine as an unknown drug and comes back clean.

/**
 * An AI suggestion → a نسخه row, with a REAL formulary drug where one exists.
 *
 * This lookup is the whole reason adding a suggestion is not one line. The
 * interaction sweep keys on `Drug.id` SLUGS (lib/prescription.js says so at the
 * top): a row carrying a synthesized id reaches the rule engine as an unknown
 * drug and comes back CLEAN — so a suggestion added blind would silently opt
 * itself out of the one safety check on this pane. The doctor app's refill path
 * does the same lookup for the same reason.
 *
 * The suggestion's dosing wins over the formulary's common values: it was
 * written for this patient's problem, where `commonDosages[0]` is written for
 * nobody. A failed lookup still yields a usable row — the dentist keeps the
 * suggestion and edits it — and the synthesized id is what says «this one was
 * not swept».
 */
export async function suggestionToRxItem(suggestion) {
  const name = String(suggestion?.name || '').trim();
  if (!name) return null;
  const key = name.toLowerCase();
  let match = null;
  try {
    const data = await bffQuery(LIST_DRUGS, { query: name, limit: 10 });
    match = (data?.listDrugs || []).find((d) =>
      String(d.name || '').trim().toLowerCase() === key
      || String(d.genericName || '').trim().toLowerCase() === key) || null;
  } catch (err) {
    // A formulary outage is a suggestion the dentist keeps, not one they lose.
    console.warn('[meds] suggestion lookup failed:', err?.message);
  }
  const drug = match || {
    id: `sugg-med-${name}`, name, genericName: '', category: '',
    commonDosages: [], commonFrequencies: [],
  };
  const item = newRxItem(drug);
  return {
    ...item,
    dose: suggestion.dosage || item.dose,
    frequency: suggestion.frequency || item.frequency,
    duration: suggestion.duration || '',
    // The free-text note IS the dosing instruction the four fields cannot hold.
    // Dropping it would make «افزودن» quietly lose the only part of the advice
    // that is specific to this patient.
    note: suggestion.notes || '',
  };
}

const RxRow = ({ item, onChange, onRemove, t }) => (
  <div style={{
    border: `1px solid ${C.border}`, borderRadius: 10,
    padding: '10px 12px', marginBottom: 8, background: CT.surface,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <Pill size={15} color={C.teal} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.textPrimary }}>{item.drug.name}</div>
        <div style={{ fontSize: 11, color: C.textMuted }}>
          {[item.drug.genericName, item.drug.category].filter(Boolean).join(' · ')}
        </div>
      </div>
      <IconButton
        variant="ghost"
        size={44}
        icon={<Trash2 size={14} />}
        label={t('prescription.item.remove', { name: item.drug.name })}
        onClick={onRemove}
      />
    </div>
    {/* aria-label on every field, and it NAMES THE DRUG: the kit's FormField
        renders its label as a <span> with no htmlFor, so without this a
        four-drug prescription is sixteen unnamed boxes and «دوز» alone would
        not say whose. */}
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
      {['dose', 'frequency', 'duration', 'note'].map((field) => (
        <TextInput
          key={field}
          label={t(`prescription.item.${field}`)}
          aria-label={`${item.drug.name} — ${t(`prescription.item.${field}`)}`}
          dir="auto"
          value={item[field]}
          onChange={(e) => onChange({ ...item, [field]: e.target.value })}
        />
      ))}
    </div>
  </div>
);

export default function MedicationsPane({ visit, patient, editable, medications }) {
  const { t, formatDate } = useTranslation();
  const toast = useToast();
  // THE ONE PANE THAT FORKS ITS PRIMITIVES. نسخه already ships a phone-designed
  // picker and row (`MobileDrugSearch` opens a BottomSheet; `DrugCombobox` is an
  // absolutely-positioned popover that the mobile concept names as the wrong
  // pattern), and prescribing is the pane most likely to be used one-handed at
  // the chair. The BEHAVIOUR is identical — same `newRxItem`, same sweep, same
  // two-tap commit — only the controls differ, which is §1.1's rule exactly.
  const { isMobile } = useResponsive();
  const [items, setItems] = React.useState([]);
  const [phase, setPhase] = React.useState('idle');
  // Which suggestion is being looked up in the formulary. Per-name, so a
  // dentist pressing two in a row sees which one is working.
  const [addingSuggestion, setAddingSuggestion] = React.useState(null);
  const [sweep, setSweep] = React.useState({ loading: false, result: null, failure: null });

  const patientId = visit?.patientId;
  const appointmentId = visit?.appointmentId;

  // A STRING, not an array: what the sweep depends on is WHICH drugs are on the
  // prescription, and a string compares by value across renders. Editing a dose
  // must not re-bill it.
  const idsKey = React.useMemo(() => interactionIds(items).join('|'), [items]);
  const sweepable = canSweep(items);

  // The sweep's OTHER inputs, as strings for the same reason. `patient` is a
  // fresh object on every `useVisit` reload, so depending on it directly
  // re-fires — and re-BILLS — a credit-costing call for a re-render that
  // changed nothing the rule engine reads.
  const conditionsKey = React.useMemo(() => conditionNames(patient).join('|'), [patient]);
  const allergiesKey = React.useMemo(() => allergenNames(patient).join('|'), [patient]);

  React.useEffect(() => {
    if (!patientId || !sweepable) {
      setSweep({ loading: false, result: null, failure: null });
      return undefined;
    }
    let alive = true;
    setSweep((s) => ({ ...s, loading: true }));
    const timer = setTimeout(() => {
      bffQuery(CHECK_DRUG_INTERACTION, {
        medicationIds: idsKey.split('|').filter(Boolean),
        patientId,
        patientConditions: conditionsKey.split('|').filter(Boolean),
        patientAllergies: allergiesKey.split('|').filter(Boolean),
      })
        .then((data) => {
          if (!alive) return;
          setSweep({ loading: false, result: data?.checkDrugInteraction || null, failure: null });
        })
        .catch((err) => {
          if (!alive) return;
          setSweep({
            loading: false,
            result: null,
            failure: errorCode(err) === 'INSUFFICIENT_CREDITS' ? 'credits' : 'unavailable',
          });
        });
    }, SWEEP_DEBOUNCE_MS);
    return () => { alive = false; clearTimeout(timer); };
  }, [idsKey, patientId, conditionsKey, allergiesKey, sweepable]);

  const {
    suggestions, suggesting, suggestError, suggest, clearSuggestions,
  } = medications || {};

  // A suggestion enters the نسخه as an ORDINARY ROW — same shape, same fields,
  // same ثبت. It is not a second kind of prescription, and once it is in the
  // list the dentist edits it like any other; the only difference is that its
  // drug had to be looked up first so the interaction sweep can see it.
  const addSuggested = async (suggestion) => {
    if (addingSuggestion) return;
    setAddingSuggestion(suggestion.name);
    try {
      const item = await suggestionToRxItem(suggestion);
      if (!item) return;
      setItems((prev) => (prev.some((i) => i.drug.name === item.drug.name)
        ? prev
        : [...prev, item]));
      setPhase('idle');
    } finally {
      setAddingSuggestion(null);
    }
  };

  const addDrug = (drug) => {
    setItems((prev) => (prev.some((i) => i.drug.id === drug.id) ? prev : [...prev, newRxItem(drug)]));
    setPhase('idle');
  };
  const updateItem = (next) => {
    setItems((prev) => prev.map((i) => (i.key === next.key ? next : i)));
    setPhase('idle');
  };
  const removeItem = (key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
    setPhase('idle');
  };

  const save = async () => {
    if (phase === 'idle') {
      // A prescription is a clinical write: two deliberate clicks, not one
      // stray one. The same two-step نسخه uses.
      setPhase('confirm');
      return;
    }
    if (phase !== 'confirm') return;
    setPhase('saving');

    // allSettled, not a chain: a row the service rejects must not silently
    // discard the rows after it, and the dentist has to be told how many landed.
    const results = await Promise.allSettled(
      items.map((item) =>
        bffMutation(CREATE_MEDICATION, {
          patientId,
          input: toMedicationInput(item, { appointmentId }),
        })),
    );
    const ok = results.filter((r) => r.status === 'fulfilled').length;

    if (ok === items.length) {
      toast.success(t('prescription.saved'));
      setItems([]);
      setPhase('idle');
      // Re-read rather than appending locally: the panes are conditionally
      // mounted, so a locally-kept list is blanked by switching tabs and back
      // while the rows exist server-side — and a fabricated id cannot later
      // address the row it stands for.
      medications?.reload?.();
      return;
    }
    if (ok === 0) toast.error(t('prescription.saveFailed'));
    else toast.warn(t('prescription.savePartial', { saved: ok, total: items.length }));
    setPhase('idle');
  };

  // Server-backed, so switching to دستورات and back does not blank it — and so
  // each row carries the id the platform actually stored.
  const thisVisit = medications?.medications || [];
  const warnings = sortWarnings(sweep.result?.warnings);
  const allergies = patient?.allergies || [];
  // نسخه's own phase→label map, imported rather than re-typed as a ternary: the
  // two surfaces must ask twice in the same words, and `tests/i18n.test.js`
  // pins every phase this function can return.
  const saveLabel = t(saveLabelKey(phase));

  return (
    <div data-testid="visit-meds" data-state={items.length ? 'content' : 'empty'}>
      {allergies.length > 0 && (
        <Alert
          variant="warning"
          icon={<AlertTriangle size={15} />}
          title={t('prescription.allergies.title')}
          style={{ marginBottom: 12 }}
        >
          {allergies.map((a) => a.allergen).filter(Boolean).join(' · ')}
        </Alert>
      )}

      {editable ? (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.textSecondary, marginBottom: 6 }}>
            {t('prescription.search.label')}
          </div>
          {isMobile ? <MobileDrugSearch onAdd={addDrug} /> : <DrugCombobox onAdd={addDrug} />}

          {/* COSTS PARSINA CREDITS — a button, never automatic, and deliberately
              NOT wired to the sweep's debounce beside it: the sweep bills as the
              dentist types, and an assist that rode along with it would bill
              again for every dose keystroke. */}
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <Button
              className="min-h-11"
              variant="outline"
              onClick={suggest}
              disabled={suggesting || !suggest}
              data-testid="visit-meds-suggest"
            >
              <Sparkles size={14} aria-hidden style={{ marginInlineEnd: 5 }} />
              {suggesting ? t('visit.meds.suggesting') : t('visit.meds.suggest')}
            </Button>
          </div>
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6 }}>
            {t('visit.ai.costsCredits')}
          </div>
        </div>
      ) : (
        <Alert variant="info" style={{ marginBottom: 12 }}>{t('visit.readOnly.note')}</Alert>
      )}

      {/* ── The AI suggestions — PROPOSALS, above the نسخه they feed ─────────
          Nothing here is prescribed: «افزودن» puts a row in the list below,
          which the dentist still has to ثبت. The interaction sweep runs on that
          row like any other, which is the whole reason each one is looked up in
          the formulary first. */}
      {suggestError === 'credits' && (
        <Alert variant="info" title={t('errors.credits.title')} style={{ marginBottom: 12 }}>
          {t('errors.credits.body')}
        </Alert>
      )}
      {suggestError === 'unavailable' && (
        <Alert variant="warning" style={{ marginBottom: 12 }}>
          {t('visit.meds.suggestUnavailable')}
        </Alert>
      )}

      {suggestions && !suggestError && (
        <div style={{ marginBottom: 14 }} data-testid="visit-meds-suggestions">
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8,
            fontSize: 11.5, fontWeight: 800, color: C.textSecondary,
          }}>
            <Sparkles size={13} aria-hidden />
            {t('visit.meds.suggestions')}
            <IconButton
              variant="ghost"
              size={44}
              icon={<X size={13} />}
              label={t('visit.meds.suggestClose')}
              onClick={clearSuggestions}
              style={{ marginInlineStart: 'auto' }}
            />
          </div>

          {suggestions.items.length === 0 ? (
            <span style={{ fontSize: 12, color: C.textMuted }}>
              {t('visit.meds.noSuggestions')}
            </span>
          ) : suggestions.items.map((m) => {
            const taken = items.some((i) => i.drug.name === m.name)
              || thisVisit.some((row) => (row.display || '').trim() === m.name.trim());
            const dosing = [m.dosage, m.frequency, m.duration].filter(Boolean).join(' · ');
            return (
              <div
                key={m.name}
                style={{
                  border: `1px dashed ${C.border}`, borderRadius: 10,
                  padding: '9px 11px', marginBottom: 8, background: CT.surface,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Pill size={15} color={C.teal} aria-hidden />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }} dir="auto">
                      {m.name}
                    </div>
                    {dosing && (
                      <div style={{ fontSize: 11, color: C.textMuted }} dir="auto">{dosing}</div>
                    )}
                  </div>
                  {editable && (
                    <Button
                      className="min-h-11"
                      variant="subtle"
                      onClick={() => addSuggested(m)}
                      disabled={taken || !!addingSuggestion}
                      data-testid={`visit-meds-suggest-add-${m.name}`}
                    >
                      {taken
                        ? t('visit.meds.suggestAdded')
                        : addingSuggestion === m.name
                          ? t('common.actions.saving')
                          : t('visit.meds.suggestAdd')}
                    </Button>
                  )}
                </div>
                {m.notes && (
                  <div
                    style={{ fontSize: 12, color: C.textSecondary, marginTop: 6, lineHeight: 1.8 }}
                    dir="auto"
                  >
                    {m.notes}
                  </div>
                )}
              </div>
            );
          })}

          {suggestions.followUpTimeline && (
            <div style={{ fontSize: 11.5, color: C.textSecondary, marginTop: 4 }} dir="auto">
              {t('visit.meds.followUp')} {suggestions.followUpTimeline}
            </div>
          )}
        </div>
      )}

      {items.map((item) => (isMobile ? (
        <MobileRxCard
          key={item.key}
          item={item}
          onChange={updateItem}
          onRemove={() => removeItem(item.key)}
          testId={`visit-rx-${item.key}`}
        />
      ) : (
        <RxRow
          key={item.key}
          item={item}
          t={t}
          onChange={updateItem}
          onRemove={() => removeItem(item.key)}
        />
      )))}

      {items.length > 0 && (
        <>
          {/* The one region here whose content arrives BY ITSELF, a second or
              two after a drug is added, while the dentist is typing a dose
              somewhere else. Without a live region a screen reader never
              mentions that a high-severity interaction appeared. Polite, not
              assertive: it must not interrupt mid-word, and ثبت is never
              blocked either way. */}
          <div
            aria-live="polite"
            aria-busy={sweep.loading || undefined}
            style={{ marginBottom: 12 }}
          >
            {sweep.loading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Spinner size={15} />
                <span style={{ fontSize: 12, color: C.textMuted }}>
                  {t('prescription.interactions.checking')}
                </span>
              </div>
            )}
            {!sweep.loading && sweep.failure === 'credits' && (
              <Alert variant="info" title={t('errors.credits.title')}>
                {t('errors.credits.body')}
              </Alert>
            )}
            {!sweep.loading && sweep.failure === 'unavailable' && (
              <span style={{ fontSize: 12, color: C.textMuted }}>
                {t('prescription.interactions.unavailable')}
              </span>
            )}
            {/* Below two distinct drugs there is nothing to interact, and the
                sweep COSTS CREDITS — so it does not run, and saying so is what
                stops «هیچ تداخلی نیست» being read into a check that never
                happened. نسخه shows the same line for the same reason. */}
            {!sweepable && (
              <span style={{ fontSize: 12, color: C.textMuted }}>
                {t('prescription.interactions.needsTwo')}
              </span>
            )}
            {!sweep.loading && !sweep.failure && sweepable && warnings.length === 0 && (
              <span style={{ fontSize: 12, color: CT.goodInk }}>
                {t('prescription.interactions.none')}
              </span>
            )}
            {warnings.map((w, i) => {
              const sev = severityKey(w.severity);
              const tint = SEVERITY_TINT[sev];
              const typeKey = warningTypeKey(w.warningType);
              return (
                <div
                  key={`${w.drugPair || w.drug || 'w'}-${i}`}
                  style={{
                    background: tint.bg, color: tint.ink, borderRadius: 9,
                    padding: '9px 11px', marginBottom: 6,
                  }}
                >
                  <div style={{ fontSize: 11.5, fontWeight: 800, marginBottom: 3 }}>
                    {t(`prescription.interactions.severity.${sev}`)}
                    {typeKey && ` · ${t(`prescription.interactions.type.${typeKey}`)}`}
                  </div>
                  {w.drugPair && (
                    // The PAIR is the actionable half of an interaction
                    // warning: "moderate" without naming which two drugs is
                    // not something a dentist can act on. LTR — these are Latin
                    // slugs inside an RTL line.
                    <div
                      dir="ltr"
                      style={{ unicodeBidi: 'isolate', fontSize: 11, marginBottom: 3 }}
                    >
                      {w.drugPair}
                    </div>
                  )}
                  <div style={{ fontSize: 12.5, lineHeight: 1.8 }}>{w.description}</div>
                </div>
              );
            })}
          </div>

          <Button className="min-h-11" onClick={save} disabled={phase === 'saving'}>{saveLabel}</Button>
        </>
      )}

      {items.length === 0 && thisVisit.length === 0 && (
        <EmptyState
          icon={<Pill size={40} color={C.textMuted} />}
          title={t('visit.meds.empty.title')}
          description={t('visit.meds.empty.description')}
        />
      )}

      {/* What this VISIT has already prescribed, and separately what the
          patient is on. Two different questions; two different lists. */}
      {thisVisit.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11.5, fontWeight: 800, color: C.textSecondary, marginBottom: 6 }}>
            {t('visit.meds.thisVisit')}
          </div>
          {thisVisit.map((m) => (
            <div key={m.id} style={{ fontSize: 12.5, color: C.textPrimary, marginBottom: 4 }}>
              <Badge variant="outline">{m.display}</Badge>{' '}
              <span style={{ color: C.textMuted }}>{savedDosageText(m)}</span>
            </div>
          ))}
        </div>
      )}

      {(patient?.medications || []).length > 0 && (
        <div style={{ marginTop: 14, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
          <div style={{ fontSize: 11.5, fontWeight: 800, color: C.textSecondary, marginBottom: 6 }}>
            {t('visit.meds.current')}
          </div>
          {(patient.medications || []).slice(0, 10).map((m) => (
            <div key={m.id} style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: C.textPrimary }}>{m.display}</div>
              <div style={{ fontSize: 11, color: C.textMuted }}>
                {m.authoredOn
                  ? t('patient.history.prescribedOn', { date: formatDate(m.authoredOn, 'numeric') })
                  : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
